import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { TabsProps } from '@chakra-ui/react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { DataProps, usePartitionedChildren } from '../../utils';
import { QTab, QTabList } from './TabsOptions';
import { QTabs } from './Tabs';
import { QBox, QFlex } from '../../QLayouts';

export type QRouteTabsProps = Pick<TabsProps, 'orientation' | 'isLazy'> & {
  variant?: 'enclosed';
  sticky?: boolean;
  persistSearchParams?: boolean;
  children?: ReactNode;
};

export type QRouteTabsItemProps = {
  route: string;
  children: ReactNode;
} & DataProps;

export const QRouteTabs: FC<QRouteTabsProps> & {
  Item: FC<QRouteTabsItemProps>;
} = ({ children, sticky = true, persistSearchParams = false, ...rest }) => {
  const { QRouteTabs_Item: items, unmatched } = usePartitionedChildren(
    children,
    'QRouteTabs_Item',
  );

  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const currentTabIndex = useMemo(() => {
    return items.findIndex((item) =>
      pathname.includes(
        (item.props as QRouteTabsItemProps).route.replace(/^\.*\//, '/'),
      ),
    );
  }, [items, pathname]);

  const goToTab = useCallback(
    (i: number) => {
      if (i !== currentTabIndex) {
        const route = (items[i].props as QRouteTabsItemProps).route;
        navigate(persistSearchParams ? route + search : route);
      }
    },
    [navigate, search, items, currentTabIndex],
  );

  useEffect(() => {
    if (currentTabIndex === -1) {
      goToTab(0);
    }
  }, [currentTabIndex, goToTab]);

  return (
    <QFlex w="100%" direction="column">
      <QTabs
        {...rest}
        index={currentTabIndex !== -1 ? currentTabIndex : 0}
        onChange={goToTab}
        sticky={sticky}
      >
        <QTabList bgColor="white">
          {items.map((t) => {
            const { route, children, ...rest } = t.props as QRouteTabsItemProps;
            return (
              <QTab key={route} {...rest}>
                {children}
              </QTab>
            );
          })}
          {unmatched}
        </QTabList>
      </QTabs>
      <QBox w="100%" py={4}>
        <Outlet />
      </QBox>
    </QFlex>
  );
};

QRouteTabs.displayName = 'QRouteTabs';
QRouteTabs.Item = () => null;
QRouteTabs.Item.displayName = 'QRouteTabs_Item';
